import { Spinner } from '@pypestream/design-system';
import { FC } from 'react';
import { Navigate, Route } from 'react-router';

import { useUserCtxSelector } from '../xstate/app.xstate';

/**
 * Sends an org-less path to the same path under the logged-in user's default
 * org, `/organization/<defaultOrgId>/<path>`.
 *
 * The query string is NOT carried. `RouteDataProvider` performs the same
 * org-scoping for any org-less path once `selectedOrgId` is known, and it
 * navigates to `/organization/<orgId><pathname>` without the query. Its
 * effect sits in `MainLayout`, a parent of this route, so in a commit where
 * both fire it runs after this one and can drop a query carried here.
 */
const OrgLessRedirect: FC<{ path: string }> = ({ path }) => {
  const { loggedInUserOrgId } = useUserCtxSelector((ctx) => ({
    loggedInUserOrgId: ctx.user?.defaultAccount?.defaultOrgId,
  }));

  return loggedInUserOrgId ? (
    // `replace`: this is a redirect, not a page. Pushed, it stays in history
    // and Back lands on it, which pushes forward again — the same trap the
    // old users/:user_id redirect had before PSTUDIO-4095 replaced it.
    <Navigate to={`/organization/${loggedInUserOrgId}/${path}`} replace />
  ) : (
    <Spinner fullpage />
  );
};

/**
 * The org-less entry points, children of the root route in `router.tsx`.
 *
 * A path needs an entry here to be reachable without an org: an org-less URL
 * with no matching route never renders `MainLayout`, so `RouteDataProvider`'s
 * org-scoping never runs for it and React Router shows its default 404.
 *
 * /my-account is the org-less entry used from outside Manager
 * (design-system's handleGoToMyAccount, which the "no access to this project"
 * screen links to, and Pro Studio's user menu). It stays a redirect rather
 * than a second mount of the page: RouteDataProvider rewrites every org-less
 * path to /organization/<selectedOrgId>/... the moment the org is known, so
 * mounting the page here would load the profile, be unmounted a frame later,
 * and load it again — three GraphQL calls twice, and a discardUpdates in
 * between. Redirecting sends the user straight to the canonical URL.
 *
 * /access-setup (PSTUDIO-4098 S61) showed the raw 404. No code in this
 * repository builds this org-less URL, and whether an outside initiator
 * should is still open, so it lands on the org-scoped page WITHOUT
 * `projectId` (see above: the query is not carried), which renders the
 * no-access view with its Home and My Account actions and fires no role
 * assignment (`AccessSetupPage`, the `!projectId` branch). Starting a grant
 * from this link is a product decision this entry does not make.
 */
export const renderOrgLessRoutes = () => [
  <Route
    path="my-account"
    element={<OrgLessRedirect path="my-account" />}
    key="MyAccount-new"
  />,
  <Route
    path="access-setup"
    element={<OrgLessRedirect path="access-setup" />}
    key="AccessSetup-new"
  />,
];
